import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { motion } from 'motion/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Loader2 } from 'lucide-react'
import { SEO } from '@/lib/seo'
import { Button, Badge, Input, Textarea, Select } from '@/components/ui'
import { companyInfo } from '@/data/company'
import { submitContactForm } from '@/lib/api'

const contactSchema = z.object({
  firstName: z.string().min(2, 'First name is required'),
  lastName: z.string().min(2, 'Last name is required'),
  email: z.string().email('Please enter a valid email'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  company: z.string().min(2, 'Company name is required'),
  industry: z.string().min(1, 'Please select an industry'),
  services: z.array(z.string()).min(1, 'Please select at least one service'),
  message: z.string().min(10, 'Please provide more details about your needs'),
})

type ContactFormData = z.infer<typeof contactSchema>

interface TrackingContext {
  page_url: string
  referrer: string
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_term: string
  utm_content: string
}

const industryOptions = [
  { value: '', label: 'Select your industry' },
  { value: 'credit-union', label: 'Credit Union' },
  { value: 'bank', label: 'Bank / Financial Services' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'government', label: 'Government' },
  { value: 'other', label: 'Other' },
]

const serviceOptions = [
  { id: 'cybersecurity', label: 'Cybersecurity' },
  { id: 'network', label: 'Network Solutions' },
  { id: 'sd-wan', label: 'SD-WAN' },
  { id: 'ucaas', label: 'UCaaS - Unified Communications' },
  { id: 'ccaas', label: 'CCaaS - Contact Center' },
  { id: 'other', label: 'Other / Not Sure' },
]

export function Contact() {
  const location = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [selectedServices, setSelectedServices] = useState<string[]>([])
  const [trackingContext, setTrackingContext] = useState<TrackingContext>({
    page_url: '',
    referrer: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
  })

  // Capture tracking context on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    setTrackingContext({
      page_url: window.location.href,
      referrer: document.referrer || '',
      utm_source: searchParams.get('utm_source') || '',
      utm_medium: searchParams.get('utm_medium') || '',
      utm_campaign: searchParams.get('utm_campaign') || '',
      utm_term: searchParams.get('utm_term') || '',
      utm_content: searchParams.get('utm_content') || '',
    })
  }, [location.search])

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      services: [],
    },
  })

  const toggleService = (serviceId: string) => {
    const newServices = selectedServices.includes(serviceId)
      ? selectedServices.filter((s) => s !== serviceId)
      : [...selectedServices, serviceId]
    setSelectedServices(newServices)
    setValue('services', newServices)
  }

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true)
    try {
      // Include tracking context with form data
      await submitContactForm({
        ...data,
        context: trackingContext,
      })
      setIsSubmitted(true)
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <SEO
        title="Contact Us"
        description="Get in touch with UC Advisors for a free security assessment and consultation on cybersecurity, network, UCaaS, CCaaS, and SD-WAN solutions."
      />

      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-30" />

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge variant="glow" className="mb-4">Contact Us</Badge>
            <h1 className="text-display-md md:text-display-lg font-heading font-bold text-white mb-4">
              Start With a Security Assessment
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Every engagement begins with understanding your security posture. Get a free consultation with our technology advisors.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="lg:col-span-1"
            >
              <div className="glass-card p-8 rounded-2xl space-y-8">
                <div>
                  <h3 className="text-xl font-heading font-semibold text-white mb-6">
                    Get in Touch
                  </h3>
                  <div className="space-y-6">
                    <a
                      href={`tel:${companyInfo.phoneRaw}`}
                      className="flex items-start gap-4 group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-accent-500/10 flex items-center justify-center group-hover:bg-accent-500/20 transition-colors">
                        <Phone className="w-5 h-5 text-accent-400" />
                      </div>
                      <div>
                        <div className="text-sm text-slate-400">Call Us</div>
                        <div className="text-white font-medium group-hover:text-accent-400 transition-colors">
                          {companyInfo.phone}
                        </div>
                      </div>
                    </a>

                    <a
                      href={`mailto:${companyInfo.email}`}
                      className="flex items-start gap-4 group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-accent-500/10 flex items-center justify-center group-hover:bg-accent-500/20 transition-colors">
                        <Mail className="w-5 h-5 text-accent-400" />
                      </div>
                      <div>
                        <div className="text-sm text-slate-400">Email Us</div>
                        <div className="text-white font-medium group-hover:text-accent-400 transition-colors">
                          {companyInfo.email}
                        </div>
                      </div>
                    </a>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-accent-500/10 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-accent-400" />
                      </div>
                      <div>
                        <div className="text-sm text-slate-400">Business Hours</div>
                        <div className="text-white font-medium">{companyInfo.hours}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-8">
                  <h4 className="text-lg font-heading font-semibold text-white mb-4">
                    Our Locations
                  </h4>
                  <div className="space-y-4">
                    {companyInfo.locations.map((location) => (
                      <div key={location.city} className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-accent-400 mt-0.5" />
                        <div>
                          <div className="text-white font-medium">
                            {location.city}, {location.state}
                          </div>
                          <div className="text-sm text-slate-400">{location.address}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="lg:col-span-2"
            >
              <div className="glass-card p-8 rounded-2xl">
                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-20 h-20 mx-auto rounded-full bg-success-500/20 flex items-center justify-center mb-6">
                      <CheckCircle className="w-10 h-10 text-success-500" />
                    </div>
                    <h3 className="text-2xl font-heading font-semibold text-white mb-4">
                      Thank You!
                    </h3>
                    <p className="text-slate-400 mb-8">
                      We've received your message and will get back to you within 24 hours.
                    </p>
                    <Button onClick={() => setIsSubmitted(false)} variant="secondary">
                      Send Another Message
                    </Button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <Input
                        label="First Name *"
                        placeholder="John"
                        {...register('firstName')}
                        error={errors.firstName?.message}
                      />
                      <Input
                        label="Last Name *"
                        placeholder="Smith"
                        {...register('lastName')}
                        error={errors.lastName?.message}
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <Input
                        label="Email *"
                        type="email"
                        placeholder="john@company.com"
                        {...register('email')}
                        error={errors.email?.message}
                      />
                      <Input
                        label="Phone *"
                        type="tel"
                        placeholder="(555) 123-4567"
                        {...register('phone')}
                        error={errors.phone?.message}
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <Input
                        label="Company *"
                        placeholder="Your Company Name"
                        {...register('company')}
                        error={errors.company?.message}
                      />
                      <Select
                        label="Industry *"
                        options={industryOptions}
                        {...register('industry')}
                        error={errors.industry?.message}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-3">
                        Services You're Interested In *
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {serviceOptions.map((service) => (
                          <button
                            key={service.id}
                            type="button"
                            onClick={() => toggleService(service.id)}
                            className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                              selectedServices.includes(service.id)
                                ? 'bg-accent-500/20 text-accent-400 border border-accent-500/50'
                                : 'bg-white/5 text-slate-400 border border-white/10 hover:border-white/20'
                            }`}
                          >
                            {service.label}
                          </button>
                        ))}
                      </div>
                      {errors.services && (
                        <p className="text-sm text-error-500 mt-2">
                          {errors.services.message}
                        </p>
                      )}
                    </div>

                    <Textarea
                      label="Tell us about your needs *"
                      placeholder="What challenges are you facing? What are your goals?"
                      {...register('message')}
                      error={errors.message?.message}
                    />

                    <Button
                      type="submit"
                      size="lg"
                      variant="glow"
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Request Security Assessment
                        </>
                      )}
                    </Button>

                    <p className="text-sm text-slate-500 text-center">
                      By submitting this form, you agree to our{' '}
                      <a href="/privacy" className="text-accent-400 hover:underline">
                        Privacy Policy
                      </a>
                      .
                    </p>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}
